import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { Loader } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { useMemo, useState, useEffect } from 'react'

import {
  useQueryGetAllBookings,
  useQueryGetAllCustomers,
  useQueryGetAllProducts,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type CoreCustomer,
  type CoreProduct,
  type CoreBooking,
  type UNSAFE_any,
} from '@flecto/types'
import { getImageUrl, useItemCategoriesInfo } from '@flecto/utils'

import { Icon } from '../../../../Icon/Icon'
import { useFilterStore } from '../../../store/useFilterStore'
import { type SelectedOptionsState } from '../../../store/useFilterStore'
import styles from '../../../styles.module.css'
import {
  type FilterField,
  type FilterOption,
  type FilterValue,
} from '../filterTypeConfig'
import { MultiSelectQueryOption } from './MultiSelectQueryOption'

type HandleFilterChange = (key: string, value: string | string[]) => void

const FIELD_CONFIG = {
  customers: {
    placeholder: 'multiselect.placeholder_customer',
    queryHook: useQueryGetAllCustomers,
    formatOption: (customer: CoreCustomer): FilterOption => ({
      label: customer.full_name,
      value: String(customer.id),
      description: [
        customer.email,
        customer.phone_number,
        customer.vat_number ? `VAT: ${customer.vat_number}` : '',
      ]
        .filter(Boolean)
        .join(' | '),
      badgeColor: 'brand',
    }),
  },
  products: {
    placeholder: 'multiselect.placeholder_product',
    queryHook: useQueryGetAllProducts,
    formatOption: (
      product: CoreProduct,
      getCategoryById: (id: string) => UNSAFE_any
    ): FilterOption => {
      const url = product?.main_photo
      const image = url && getImageUrl(url)
      const category = getCategoryById(String(product.category_id))
      return {
        label: product.title,
        value: String(product.id),
        description: category?.name || '',
        badgeColor: 'brand',
        imageUrl: image,
      }
    },
  },
  bookings: {
    placeholder: 'multiselect.placeholder_bookings',
    queryHook: useQueryGetAllBookings,
    formatOption: (booking: CoreBooking): FilterOption => ({
      label: `#${booking.internal_id}`,
      value: String(booking.internal_id),
    }),
  },
}

export const createMultiSelectQueryProps = (
  field: FilterField,
  selectedFilters: FilterValue[],
  handleFilterChange: HandleFilterChange,
  storeId: string
) => {
  const { t } = useTranslation('shared', { keyPrefix: 'filters' })
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)
  const { selectedOptions, setSelectedOptions } = useFilterStore(storeId)
  const selectedOptionsCache =
    selectedOptions[field.key as keyof SelectedOptionsState] || []

  const config = FIELD_CONFIG[field.key as keyof typeof FIELD_CONFIG]
  const { getCategoryById } =
    field.key === 'products'
      ? useItemCategoriesInfo()
      : { getCategoryById: () => undefined }

  const { data: rawData, isLoading } = config.queryHook({
    q: debouncedSearchTerm,
    order: 'asc',
  })

  const placeholderMap = {
    'multiselect.placeholder_product': {
      dataKey: 'product',
      formatter: config.formatOption,
    },
    'multiselect.placeholder_customer': {
      dataKey: 'customer',
      formatter: config.formatOption,
    },
    'multiselect.placeholder_bookings': {
      dataKey: 'booking',
      formatter: config.formatOption,
    },
  }

  const searchOptions = useMemo(() => {
    const mapping =
      placeholderMap[config.placeholder as keyof typeof placeholderMap]
    if (!mapping) return []

    const data = (rawData as Record<string, unknown>)?.[mapping.dataKey]
    if (!Array.isArray(data)) return []

    return data.map((item: UNSAFE_any) =>
      mapping.formatter(item, getCategoryById)
    )
  }, [rawData, field.key, getCategoryById])

  const currentOptions = useMemo(
    () =>
      (searchOptions ?? []).map((option: FilterOption) => ({
        ...option,
        ...(option.imageUrl ? { imageUrl: option.imageUrl } : {}),
      })),
    [searchOptions]
  )

  // Updates the cache of selected options by adding new selections and updating existing ones based on the current options and selected values for the current field

  const updateSelectedOptionsCache = () => {
    const selectedValues =
      (selectedFilters.find((f) => f.key === field.key)?.value as string[]) ||
      []

    const newCache = [...selectedOptionsCache]
    currentOptions.forEach((option: UNSAFE_any) => {
      if (selectedValues.includes(option.value)) {
        const cacheIndex = newCache.findIndex(
          (cached) => cached.value === option.value
        )
        if (cacheIndex === -1) {
          newCache.push(option)
        } else {
          newCache[cacheIndex] = option
        }
      }
    })

    setSelectedOptions(field.key as keyof SelectedOptionsState, newCache)
  }

  useEffect(() => {
    updateSelectedOptionsCache()
  }, [currentOptions, selectedFilters, field.key])

  const sanitizedData = useMemo(() => {
    const uniqueOptions = new Map()

    // Prioritize current options
    currentOptions.forEach((option: UNSAFE_any) => {
      uniqueOptions.set(option.value, option)
    })

    // Add cached options only if they don't exist
    selectedOptionsCache.forEach((option) => {
      if (!uniqueOptions.has(option.value)) {
        uniqueOptions.set(option.value, option)
      }
    })

    return Array.from(uniqueOptions.values())
  }, [currentOptions, selectedOptionsCache])

  const baseProps = {
    classNames: {
      dropdown: styles.filterMultiSelectDropdown,
      input: styles.filterMultiSelectInput,
      options: styles.filterOptions,
    },
    rightSection: <Icon icon={faChevronDown} size='sm' />,
    placeholder: t(config.placeholder),
    checkIconPosition: field.checkIconPosition,
    nothingFoundMessage: isLoading ? (
      <Loader size='sm' />
    ) : (
      t('multiselect.nothing_found_message')
    ),
    comboboxProps: { withinPortal: false },
    searchable: field.searchable,
    renderValue: (selected: string[]) => {
      return selected
        .map((value) => {
          const option = sanitizedData.find(
            (opt: UNSAFE_any) => opt.value === value
          )
          if (!option) return value
          return `${option.label} (#${value})`
        })
        .join(', ')
    },
    data: sanitizedData,
    withCheckIcon: true,
    filter: ({ options }: { options: typeof sanitizedData }) => options,
    onSearchChange: setSearchTerm,
    value:
      (selectedFilters.find((f) => f.key === field.key)?.value as string[]) ||
      [],
    onChange: (value: string | string[]) =>
      handleFilterChange(field.key, value),
  }
  return {
    ...baseProps,
    renderOption: ({
      option,
      checked,
    }: {
      option: FilterOption
      checked?: boolean
    }) => (
      <MultiSelectQueryOption
        type={field.type}
        option={{
          label: option.label,
          value: option.value,
          checked,
          description: option.description,
          imageUrl: option.imageUrl,
        }}
      />
    ),
  }
}
